/**
 * 🎙️ **읽어주기(더빙) 판정** — 어느 쪽이 소리를 갖고 있고, 어느 쪽은 왜 못 만드는가.
 *
 * ## 왜 판정을 여기 두나
 * 같은 물음을 **세 곳**이 한다: 관리자 [🎙️ 읽어주기] 화면, 출시 준비 목록, `pnpm book:audio`.
 * 화면마다 다시 세면 **아무 에러 없이 갈린다** — 관리자엔 「34/34 다 됐어요」인데 아이는
 * 8쪽에서 소리가 끊기는 식이다. 이 저장소가 이미 겪은 종류의 사고다(`previewParity.test.ts`).
 *
 * ## 「없음」을 두 가지로 나눈다
 * 소리가 없는 칸에는 **성질이 다른 두 경우**가 섞여 있다:
 *
 * - `missing` — 자막은 있는데 소리를 아직 안 만들었다. **버튼을 누르면 된다.**
 * - `no_text` — 읽을 자막 자체가 없다. **버튼을 눌러도 아무 일이 안 일어난다.**
 *
 * 이 둘을 「없음」으로 뭉치면 운영자는 눌러 보고, 아무 일도 안 생기고, 왜인지 알 수 없다.
 * 한동안 그런 칸이 **20개**였다 —「나무 위 도서관」12쪽·「경주」8쪽에 영어 자막이 아예 없어서,
 * 운영자가 [🎙️ 읽어주기]에서 영어를 눌러도 아무 일이 안 일어났다.
 * **2026-09-07 에 그 20쪽의 영어 자막을 채워 0개가 됐다** — 이제 누르면 만들어진다.
 * (숫자를 여기 적어 두면 이렇게 옛말이 된다. 진짜 수는 `dubCount` 가 세는 것이 맞다.)
 *
 * ## 글자 없는 쪽은 「할 일」이 아니다
 * 그림만 있는 면(별밤 한 컷·뒤표지)은 한국어도 영어도 자막이 없다. 그건 덜 된 게 아니라
 * **원래 그런 쪽**이다. 그래서 세지 않는다 — 늘 뭔가 빨간 목록은 곧 전부 무시당한다.
 */

/** 읽어주기가 지원하는 말. 자막과 같은 두 갈래다(FR-BOOK-03). */
export type DubLang = "ko" | "en";
export const DUB_LANGS: readonly DubLang[] = ["ko", "en"];

/** 한 칸의 상태. */
export type DubState =
  /** 소리가 있다 — 들어 볼 수 있고, 다시 만들 수도 있다. */
  | "done"
  /** 자막은 있는데 소리가 없다 — **여기가 만들 대상**이다. */
  | "missing"
  /** 읽을 자막이 없다 — 소리를 만들 수 없다(먼저 자막을 채워야 한다). */
  | "no_text"
  /**
   * 🔁 **소리는 있는데 지금 자막과 다른 글로 만든 것** — 다시 만들 대상이다.
   *
   * 🔴 (2026-09-10) 《폴짝》 세 쪽 자막이 스캔본보다 짧았다. 자막을 고쳐도 소리는 **옛 글 그대로**
   *    남는다 — 「이미 있는 것은 다시 안 만든다」가 규칙이라서. 화면 글과 들리는 말이 갈린다.
   *    그래서 소리에 **어떤 글로 만들었나**(`fromText`)를 적어 두고, 자막과 다르면 이 상태로 본다.
   *    적어 둔 것이 없는 옛 소리는 알 길이 없어 `done` 으로 둔다(모르는 것을 낡았다고 하지 않는다).
   */
  | "stale";

/** 판정에 필요한 만큼의 페이지. 뷰어의 `BookPage` 를 그대로 받아도 된다. */
export interface DubPageInput {
  id: string;
  subtitle_ko?: string;
  subtitle_en?: string;
}

/** 한 쪽 한 줄. 화면은 이걸 그대로 그린다. */
export interface DubRow {
  pageId: string;
  /** 사람에게 보여줄 쪽 번호(표지는 0). */
  index: number;
  text: Record<DubLang, string>;
  state: Record<DubLang, DubState>;
  /** 그림만 있고 글자가 하나도 없는 면 — 할 일로 세지 않는다. */
  wordless: boolean;
}

/** 만들 대상 하나. 순서대로 돌리기 좋게 납작하게 편다. */
export interface DubTarget {
  pageId: string;
  lang: DubLang;
  text: string;
  /** 매니페스트 키 — 여기에 mp3 를 올린다. */
  key: string;
}

/**
 * 읽어주기 mp3 가 들어갈 자리. `book.audio.<책>.<쪽>.<말>` — 뷰어(`bookData.loadBook`)가
 * 읽는 바로 그 키다. 여기서 한 글자라도 다르면 만들어 놓고도 아이 화면에서 안 들린다.
 */
export function bookAudioKey(bookId: string, pageId: string, lang: DubLang): string {
  return `book.audio.${bookId}.${pageId}.${lang}`;
}

/**
 * 아주 오래된 콘텐츠가 쓰던 **책 이름 없는 키**. 뷰어가 아직 폴백으로 읽으므로
 * 「있다/없다」를 셀 때도 같이 봐야 한다 — 안 그러면 이미 있는 소리를 또 만든다(돈이 든다).
 */
export function bookAudioLegacyKey(pageId: string, lang: DubLang): string {
  return `book.audio.${pageId}.${lang}`;
}

const trim = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

/** 한 권의 더빙 상태표. `has` 는 「그 키에 파일이 있나」 — 매니페스트든 R2 목록이든 상관없다. */
export function dubRows(
  bookId: string,
  pages: readonly DubPageInput[],
  has: (key: string) => boolean,
  /** 그 소리를 **어떤 글로 만들었나**(매니페스트 항목의 `fromText`). 모르면 undefined. */
  sourceOf?: (key: string) => string | undefined,
): DubRow[] {
  return pages.map((p, i) => {
    const text: Record<DubLang, string> = {
      ko: trim(p.subtitle_ko),
      en: trim(p.subtitle_en),
    };
    const stateOf = (lang: DubLang): DubState => {
      const key = bookAudioKey(bookId, p.id, lang);
      if (has(key) || has(bookAudioLegacyKey(p.id, lang))) {
        // 🔁 적어 둔 글이 있고 지금 자막과 다르면 낡은 소리다(적어 둔 게 없으면 모른다 — done)
        const from = sourceOf?.(key);
        if (typeof from === "string" && text[lang] && trim(from) !== text[lang]) return "stale";
        return "done";
      }
      return text[lang] ? "missing" : "no_text";
    };
    return {
      pageId: p.id,
      index: i,
      text,
      state: { ko: stateOf("ko"), en: stateOf("en") },
      wordless: !text.ko && !text.en,
    };
  });
}

/** 한 말의 「몇 개 중 몇 개」. 자막이 없어 못 만드는 칸은 **분모에서 뺀다** — 100% 가 될 수 있어야 한다. */
export interface DubCount {
  done: number;
  /** 만들 칸 — 소리가 없는 칸과 **낡은 칸(stale)** 을 함께 센다(둘 다 아이가 들을 말이 틀리다). */
  missing: number;
  /** 그중 낡은 칸 — 화면이 「자막이 바뀌어 다시 만들 칸 N」으로 따로 말한다. */
  stale: number;
  /** 자막이 없어 지금은 만들 수 없는 칸. 따로 보여 준다(할 일이 아니라 막힌 것). */
  blocked: number;
}

export function dubCount(rows: readonly DubRow[], lang: DubLang): DubCount {
  let done = 0;
  let missing = 0;
  let stale = 0;
  let blocked = 0;
  for (const r of rows) {
    if (r.wordless) continue; // 그림만 있는 면은 애초에 셈에서 뺀다
    const s = r.state[lang];
    if (s === "done") done++;
    else if (s === "missing") missing++;
    else if (s === "stale") {
      missing++; // 🔴 낡은 소리는 「다 됐다」가 아니다 — 100% 로 보이면 아무도 다시 안 만든다
      stale++;
    } else blocked++;
  }
  return { done, missing, stale, blocked };
}

/**
 * 실제로 돌릴 목록을 만든다.
 *
 * @param langs  만들 말(한국어만·영어만·둘 다)
 * @param only   고른 쪽만. 비었으면 전부.
 * @param redo   `true` 면 이미 있는 것도 다시 만든다. 기본은 **빠진 것만** —
 *               이미 있는 걸 또 만들면 돈이 들고, 같은 목소리라도 억양이 미묘하게 달라진다.
 *
 * 순서는 **책에 실린 순서 그대로**다. 중간에 멈췄을 때 「어디까지 됐는지」가 눈에 보여야 한다.
 */
export function dubTargets(
  bookId: string,
  rows: readonly DubRow[],
  langs: readonly DubLang[],
  opts: { only?: readonly string[]; redo?: boolean } = {},
): DubTarget[] {
  const pick = opts.only && opts.only.length > 0 ? new Set(opts.only) : null;
  const out: DubTarget[] = [];
  for (const r of rows) {
    if (pick && !pick.has(r.pageId)) continue;
    for (const lang of langs) {
      if (!langs.includes(lang)) continue;
      const s = r.state[lang];
      if (s === "no_text") continue; // 읽을 글이 없다 — 눌러도 안 되는 걸 목록에 넣지 않는다
      if (s === "done" && !opts.redo) continue; // 낡은 칸(stale)은 **기본으로 넣는다** — 다시 만들 대상이다
      out.push({ pageId: r.pageId, lang, text: r.text[lang], key: bookAudioKey(bookId, r.pageId, lang) });
    }
  }
  return out;
}

/**
 * 엣지 TTS 는 분당 20개로 막혀 있다(`apps/edge/src/tts.ts`). 그보다 조금 여유 있게 돌린다 —
 * 429 를 맞고 재시도하는 것보다 처음부터 천천히 가는 편이 전체로는 빠르고, 운영자에게는
 * **남은 시간이 정확해 보인다**(막히면 진행 막대가 멈춘 것처럼 보인다).
 */
export const DUB_PACE_MS = 3_400;

/** 남은 시간(초). 화면에 「약 4분」으로 적는다 — 탭을 닫으면 안 되는 시간이라 미리 알려야 한다. */
export function dubEtaSeconds(count: number): number {
  return Math.ceil((count * DUB_PACE_MS) / 1000);
}

/** 「약 4분」·「약 40초」. 분 단위가 넘으면 초는 버린다(정확한 초를 보여 줄 이유가 없다). */
export function dubEtaLabel(count: number): string {
  const s = dubEtaSeconds(count);
  if (s < 60) return `약 ${Math.max(5, Math.round(s / 5) * 5)}초`;
  return `약 ${Math.ceil(s / 60)}분`;
}

/**
 * 🎙️ **목소리** — 책 한 권은 처음부터 끝까지 한 목소리여야 한다.
 *
 * 예전에는 쪽을 열 때마다 고르개가 기본값으로 돌아갔다. 그래서 나중에 한 쪽만 다시 만들면
 * **그 쪽만 목소리가 달라졌고**, 화면·파일에는 아무 이상이 없어 사람이 16쪽을 다 들어야 알았다.
 * 그래서 책 문서에 적어 둔다(`narrationVoice`).
 */
export const DUB_VOICES = [
  { id: "nova", label: "따뜻한 (기본)" },
  { id: "shimmer", label: "밝은" },
  { id: "fable", label: "이야기꾼" },
  { id: "coral", label: "부드러운" },
  { id: "onyx", label: "낮은 목소리" },
] as const;

export type DubVoice = (typeof DUB_VOICES)[number]["id"];
export const DEFAULT_DUB_VOICE: DubVoice = "nova";

/** 저장된 값이 우리가 아는 목소리인가(불변식 7 — 밖에서 온 값을 그대로 믿지 않는다). */
export function normalizeDubVoice(v: unknown): DubVoice {
  return DUB_VOICES.some((x) => x.id === v) ? (v as DubVoice) : DEFAULT_DUB_VOICE;
}

/**
 * 💾 **내려받을 때 쓸 파일 이름** — 탐색기에서 **순서대로 서고, 보면 안다.**
 *
 * `00_표지_한국어.mp3` · `01쪽_한국어.mp3` … `33쪽_English.mp3`
 *
 * 쪽 번호를 **두 자리로 채운다**. 안 채우면 탐색기가 `10쪽` 을 `2쪽` 앞에 세운다 —
 * 이 저장소는 이미 파일 이름 정렬로 한 번 미끄러졌다(스캔본 순서, 2026-08-23).
 * 말은 `ko/en` 이 아니라 「한국어/English」로 적는다. 파일을 여는 사람은 개발자가 아니다.
 */
export function dubFileName(index: number, lang: DubLang): string {
  const who = lang === "ko" ? "한국어" : "English";
  if (index === 0) return `00_표지_${who}.mp3`;
  return `${String(index).padStart(2, "0")}쪽_${who}.mp3`;
}

/**
 * 묶음 안에 같이 넣는 **목록표**. 파일만 서른네 개 받으면 어느 소리가 어느 문장인지
 * 알 수 없다 — 나중에 영상·QR 에 붙일 때 이 표가 있어야 짝을 찾는다.
 *
 * 엑셀이 UTF-8 CSV 를 한글로 못 읽는 일이 잦아 **BOM 을 붙이는 건 호출부의 몫**으로 두고,
 * 여기서는 내용만 만든다(테스트가 쉬워진다).
 */
export function dubIndexCsv(rows: readonly DubRow[], langs: readonly DubLang[]): string {
  const esc = (s: string) => `"${s.replace(/"/g, '""')}"`;
  const out = ["쪽,말,파일,자막"];
  for (const r of rows) {
    for (const lang of langs) {
      if (r.state[lang] !== "done") continue;
      const no = r.index === 0 ? "표지" : `${r.index}쪽`;
      out.push([esc(no), esc(lang === "ko" ? "한국어" : "English"), esc(dubFileName(r.index, lang)), esc(r.text[lang])].join(","));
    }
  }
  return out.join("\r\n") + "\r\n";
}
